import React, { useMemo } from 'react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { changeInfoSchema } from 'types/validationSchemes';
import Button from 'components/Button';
import Paper from 'components/Paper';
import Input from 'components/Input';
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from 'api/services/dataEndpoints';
import type { SettingItemType, UpdateSettingsPayloadType } from 'types/apiTypes/settingsApiTypes';
import StyledInfoSection from '../InfoSection/InfoSection.styles';

const InfoSection: React.FC = () => {
  const { data, isFetching, isLoading: isDataLoading, isError } =
    useGetSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateSettingsMutation();

  const settings: SettingItemType[] = useMemo(
    () => (Array.isArray(data)
      ? (data as SettingItemType[])
      : ([] as SettingItemType[])),
    [data],
  );

  const initialValues = useMemo(() => {
    const acc: Record<string, string> = {};
    for (const s of settings) {
      acc[s.key] = s.value == null ? '' : String(s.value);
    }
    return acc;
  }, [settings]);

  const dynamicSchema = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shape: Record<string, any> = {};
    settings.forEach((setting) => {
      shape[setting.key] = yup.string().required('This field is required');
    });

    return yup.object(shape);
  }, [settings]);

  const formik = useFormik<Record<string, string>>({
    enableReinitialize: true,
    initialValues,
    validationSchema: dynamicSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setStatus }) => {
      setStatus(undefined);
      try {
        const changed = Object.entries(values).filter(
          ([k, v]) => initialValues[k] !== v,
        );
        const payload: UpdateSettingsPayloadType = {
          updates: changed.map(([key, value]) => ({ key, value })),
        };

        if (
          'updates' in payload &&
          Array.isArray(payload.updates) &&
          payload.updates.length === 0
        ) {
          setStatus('No changes detected');
          setSubmitting(false);
          return;
        }

        await updateSettings(payload).unwrap();
        setStatus('Saved');
      } catch (err) {
        const e = err as AxiosError<unknown>;
        const data = e.response?.data;
        if (data && typeof data === 'object') {
          let hasField = false;
          Object.entries(data).forEach(([field, message]) => {
            if (field in values && typeof message === 'string') {
              setFieldError(field, message);
              hasField = true;
            }
          });
          if (!hasField) {
            setStatus('Cannot save');
          }
        } else {
          setStatus('Cannot save');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const isBusy = isDataLoading || isFetching;
  const isDisabled = isBusy || isSaving || !formik.isValid;

  return (
    <StyledInfoSection>
      <Paper className="container">
        <form onSubmit={formik.handleSubmit} className="form-section">
          {isError && <div className="error">Cannot load settings</div>}
          {isBusy && <div className="skeleton">Loading…</div>}

          {!isBusy &&
            settings.map((s) => (
              <Input
                key={s.key}
                label={s.label ?? s.key}
                nameInput={s.key}
                onChangeValue={formik.handleChange}
                value={formik.values[s.key] ?? ''}
                tooltipText={s.tooltip}
                errorText={
                  formik.touched[s.key] ? (formik.errors)[s.key] : undefined
                }
                onBlur={formik.handleBlur}
              />
            ))}

          <div className="form-section__button-area">
            <Button
              variant="outlined"
              type="reset"
              onClick={() => formik.resetForm()}
              disabled={isBusy || isSaving}
            >
              Reset
            </Button>

            <Button
              loading={isSaving}
              variant="contained"
              type="submit"
              disabled={isDisabled}
            >
              Update
            </Button>
          </div>

          {formik.status && (
            <div className="form-status" aria-live="polite">
              {formik.status}
            </div>
          )}
        </form>
      </Paper>
    </StyledInfoSection>
  );
};

export default InfoSection;
